<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div
      v-if="overdraftAccountSuccessCount.length || overdraftAccountFailed.length"
      class="m-2"
    >
      <b-row>
        <b-col
          v-if="overdraftAccountSuccessCount"
          md="6"
          class="mb-2"
        >
          <b-alert
            show
            variant="primary"
            class="p-2"
          >
            {{ overdraftAccountSuccessCount }}
          </b-alert>
        </b-col>
        <b-col
          v-if="overdraftAccountFailed.length"
          md="6"
          class="mb-2"
        >
          <div>
            <b-button variant="danger">
              {{ $t('FAILED LISTS') }}
            </b-button>
          </div>
          <b-table
            ref="overdraftAccountFailedReport"
            :items="overdraftAccountFailed"
            responsive
            :fields="translatedFailedHeaders"
            primary-key="id"
            show-empty
            :empty-text="$t('No records available')"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            class="position-relative"
          /></b-col>
      </b-row>
    </div>

    <div class="m-2">
      <b-row>
        <b-col
          md="6"
          class="mb-2"
        >
          <validation-observer ref="dataMigrationOverdraftAccountRules">
            <b-form>
              <b-col
                md="12"
                class="mb-2"
              >
                <div class="mb-1">
                  {{ $t('Select file (.xlsx format only)') }}
                </div>
                <b-form-file
                  id="file-name"
                  v-model="hasFileData"
                  :label="$t('Select file (.xlsx format only)')"
                  :state="Boolean(hasFileData)"
                  :placeholder="$t('Choose a file or drop it here...')"
                  :drop-placeholder="$t('Drop file here...')"
                  error-name="file"
                  rules="required"
                  accept=".xlsx"
                  @change="onChangeXLSX($event)"
                />
              </b-col>
            </b-form>
          </validation-observer>
        </b-col>

        <b-col
          md="6"
        >
          <b-row>
            <b-col
              md="5"
              class="mt-2"
            >
              <Button
                v-if="getUserPermission('DATA_MIGRATION') && parseExcelFile.length"
                :label="$t('Create Account Overdraft Migration')"
                size="md"
                :loading="isLoading"
                @click="handleCreate"
              />
            </b-col>
            <b-col
              md="7"
              class="mt-2"
            >
              <Button
                class="ml-2"
                align="end"
                :label="$t('Download Account Overdraft Migration Format')"
                size="md"
                icon="DownloadIcon"
                show-icon
                :loading="isLoading2"
                :disabled="isDisabled2"
                @click="handleDownloadSubmit"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
    <p
      v-if="totalSheetRows"
      class="p-2"
      style="font-size: 18px;"
    >
      {{ $t('There are total of') }} ({{ totalSheetRows }}) {{ $t('Records fetched') }}
    </p>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BForm, BTable, BFormFile, BButton, BAlert,
} from 'bootstrap-vue'
import { required } from '@validations'
import { ValidationObserver } from 'vee-validate'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import XLSX from 'xlsx'
import Button from '../../../components/button/Button.vue'
import { toKobo } from '@/helpers/formatAmount'
import { batchAccountFormat, tableLessHeaders, tableAllHeaders } from '../../../../helpers/data'
import { toYearWithDate } from '../../../../helpers/formatDate'

export default {
  components: {
    BCard,
    BTable,
    BRow,
    BCol,
    BForm,
    BFormFile,
    ValidationObserver,
    Button,
    BButton,
    BAlert,
  },
  filters: {
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      totalSheetRows: '',
      fileData: [],
      hasFileData: [],
      parseExcelFile: [],
      sortOrders: {},
      accountFormat: batchAccountFormat,
      successHeaders: tableAllHeaders,
      failedHeaders: tableLessHeaders,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true },
        { key: 'productCode', sortable: true },
        { key: 'branchCode', sortable: true },
        { key: 'limit', sortable: true, label: 'Overdraft Limit' },
        { key: 'tenure', sortable: true, label: 'overdraftTenure' },
        { key: 'interestAmount', sortable: true, label: 'Overdraft Interest Rate' },
        { key: 'commencementDate', sortable: true, label: 'Overdraft Start Date' },
        { key: 'overdrawnBalance', sortable: true },
        { key: 'dueAndUnpaidOverdraftInterest', sortable: true },
        { key: 'dueAndUnpaidOverdraftFee', sortable: true },
      ],
      required,
      isDisabled: false,
      isLoading: false,
      isDisabled2: false,
      isLoading2: false,
      showFormError: false,
      overdraftAccountSuccessCount: [],
      overdraftAccountFailed: [],
      sortDesc: false,
      sortBy: 'newGLCode',
    }
  },
  computed: {
    translatedFailedHeaders() {
      return this.failedHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  methods: {
    ...mapActions({
      migrateOverdraftAccountCreation: 'batch/migrateOverdraftAccountCreation',
      downloadAccountFormat: 'batch/downloadAccountFormat',
    }),
    async handleCreate() {
      this.$refs.dataMigrationOverdraftAccountRules.validate().then(success => {
        if (success) {
          this.confirm(this.migrateFdAccountAction, { text: this.$t('Are you sure you want to Create Account Overdraft Migration?') })
        }
      })
    },
    async migrateFdAccountAction() {
      try {
        this.overdraftAccountSuccessCount = ''
        this.showFormError = false
        this.isDisabled = true
        this.isLoading = true
        const formData = this.parseExcelFile.map(item => ({
          ...item,
          accountNumber: item.accountNumber ? item.accountNumber.toString() : null,
          branchCode: item.branchCode ? item.branchCode.toString() : null,
          productCode: item.productCode ? item.productCode.toString() : null,
          limit: item.limit ? Number(toKobo(item.limit).toFixed()) : null,
          tenure: item.tenure ? Number(item.tenure) : null,
          commencementDate: item.commencementDate ? toYearWithDate(item.commencementDate) : null,
          interestAmount: item.interestAmount ? Number(toKobo(item.interestAmount).toFixed()) : null,
          overdrawnBalance: item.overdrawnBalance ? Number(toKobo(item.overdrawnBalance).toFixed()) : null,
          dueAndUnpaidOverdraftInterest: item.dueAndUnpaidOverdraftInterest ? Number(toKobo(item.dueAndUnpaidOverdraftInterest).toFixed()) : null,
          dueAndUnpaidOverdraftFee: item.dueAndUnpaidOverdraftFee ? Number(toKobo(item.dueAndUnpaidOverdraftFee).toFixed()) : null,
        }))
        const response = await this.migrateOverdraftAccountCreation(formData)
        const { status, data } = response
        if (status === 200) {
          this.hasFileData = []
          this.parseExcelFile = []
          if (data.successCount > 1) {
            const plural = this.$t('Overdraft Accounts was successfully created')
            this.overdraftAccountSuccessCount = `(${data.successCount}) ${plural}`
          } else {
            const singular = this.$t('Overdraft Account was successfully created')
            this.overdraftAccountSuccessCount = `(${data.successCount}) ${singular}`
          }
          this.overdraftAccountFailed = data.failed

          this.isDisabled = false
          this.isLoading = false
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const errorText = this.$t('Your request to create Batch Account Overdraft failed. Reason')
          this.alertError({ text: `${errorText}: ${responseError.errorMessage}` })
        })
        this.showFormError = true
      } finally {
        this.isDisabled = false
        this.isLoading = false
      }
    },
    onChangeXLSX(event) {
      this.fileData = event.target.files ? event.target.files[0] : null
      if (this.fileData) {
        const vm = this
        const reader = new FileReader()
        reader.onload = e => {
          const data = e.target.result
          const workbook = XLSX.read(data, { type: 'binary', cellDates: true, dateNF: 'dd/mm/yyyy;@' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const rowObject = XLSX.utils.sheet_to_row_object_array(worksheet, { raw: false })
          vm.totalSheetRows = rowObject.length
          vm.parseExcelFile = JSON.parse(JSON.stringify(rowObject)).map(items => ({
            accountNumber: items['Old Reference No (Current/Savings Account Only)'],
            branchCode: items['Branch Code'],
            productCode: items['Product Code'],
            limit: items['Overdraft Limit'],
            tenure: items['Overdraft Tenure'],
            interestAmount: items['Overdraft Interest Rate'],
            commencementDate: items['Overdraft Start Date'],
            overdrawnBalance: items['Overdrawn Balance'],
            dueAndUnpaidOverdraftInterest: items['Due & Unpaid Overdraft Interest'],
            dueAndUnpaidOverdraftFee: items['Due & Unpaid Overdraft Fees'],
          }))
        }
        reader.readAsBinaryString(this.fileData)
      }
    },
    async handleDownloadSubmit() {
      this.confirm(this.downloadAction, { text: this.$t('Are you sure you want to download Account Overdraft Migration Format?') })
    },
    async downloadAction() {
      try {
        this.isLoading2 = true
        this.isDisabled2 = true
        const OVERDRAFT_ACCOUNTS_MIGRATION_TEMPLATE = 9
        const response = await this.downloadAccountFormat(OVERDRAFT_ACCOUNTS_MIGRATION_TEMPLATE)
        if (response.status === 200) {
          const downloadAnchor = document.createElement('a')
          downloadAnchor.setAttribute('href', response.data.data)
          const filename = 'Udara360-Account-Overdraft-Migration.xlsx'
          downloadAnchor.setAttribute('download', filename)
          document.body.appendChild(downloadAnchor)
          downloadAnchor.click()
          document.body.removeChild(downloadAnchor)
          this.alertSuccess({ text: this.$t('Your request to download Account Overdraft Migration Format was successful') })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const reasonText = this.$t('Your request to download Account Overdraft Migration Format failed. Reason')
          this.alertError({ text: `${reasonText}: ${responseError.errorMessage}` })
          this.isLoading2 = false
          this.isDisabled2 = false
        })
      } finally {
        this.isLoading2 = false
        this.isDisabled2 = false
      }
    },
  },
}
</script>
